import { CommonAccountConstant, commonAccountRequiredFieldList } from "presentation/constant/CommonAccount/CommonAccountConstant";
import { statusDropdownOptions } from "presentation/constant/DropDownOptions/StaticDropdownOptions";
import { useCommonAccountVM } from "presentation/hook/CommonAccount/useCommonAccountVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { CommonAccountModel } from "presentation/model/CommonAccount/CommonAccountModel";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DatePickerComponent from "presentation/view/components/DatePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { NbisIconButton } from "presentation/view/components/NbisIconButton";
import { memo, useCallback, useMemo, useState } from "react";
import { FieldType, HPHBreadcrumb, HPHCheckbox, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { Breadcrumb, SidebarCaption, Sidebarheader, SidebarTitle, StyledAction, StyledSidebar } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const CommonAccountEditPanel = ({ commonAccountState }: { commonAccountState: CommonAccountModel }) => {
    const COMMON_ACCOUNT_CONSTANT = CommonAccountConstant.Table;
    const [isLoading, setIsLoading] = useState(false);
    // const [commonAccountState] = useCommonAccountTracked();
    const commonAccountVM = useCommonAccountVM();
    const { tempCompanyRows, currentEditRow, isAdd, isEdit, isShowCompanyAddPanel, isShowCompanyEditPanel } = commonAccountState;
    const messageBarVM = useMessageBarVM();
    const [anaInfoState] = useANAInfoTracked();
    const { allowUpdate } = anaInfoState;

    const hasDuplicateChargeAndBillToCompanies = useCallback(() => {
        const duplicates = currentEditRow.companyDetails.filter((item, index, self) =>
            self?.some((otherItem, otherIndex) =>
                otherIndex !== index &&
                item.chargeOnCompany === otherItem.chargeOnCompany &&
                item.billToCompany === otherItem.billToCompany
            )
        );

        if (duplicates.length > 0) {
            return true;
        }

        return false;
    }, [currentEditRow.companyDetails]);

    const handleSave = useCallback(async () => {
        if (!currentEditRow.activeInd) {
            messageBarVM.showError("Active Ind is mandatory.");
            return;
        }
        if (hasDuplicateChargeAndBillToCompanies()) {
            messageBarVM.showError("Duplicate record found, please check the key: Charge On Company,  Bill To Company.");
            return;
        }
        if (currentEditRow.companyDetails && currentEditRow.companyDetails.length > 0
            && currentEditRow.companyDetails.reduce((sum, item) => sum + (item.percentage ?? 0), 0) !== 100) {

            messageBarVM.showError("The sum of percentage is not equal to 100%.");
            return;
        }
        setIsLoading(true);
        const data = await commonAccountVM.onSave(currentEditRow);
        if (data && data.success) {
            messageBarVM.showSuccess("Save Successfully.");
            commonAccountVM.searchAllCommonAccountList().then((data) => {
                setIsLoading(false)
            }).catch(error => {
                setIsLoading(false)
            });
        } else {
            setIsLoading(false)
            messageBarVM.showError(data.data);
            return;
        }
    }, [commonAccountVM, currentEditRow, hasDuplicateChargeAndBillToCompanies, messageBarVM]);

    const handClose = useCallback(() => {
        if (tempCompanyRows && tempCompanyRows.length > 0) {
            const sumOfPercentage = tempCompanyRows.reduce((sum, row) => sum + Number(row.percentage), 0);

            if (sumOfPercentage !== 100) {
                messageBarVM.showError("The sum of percentage is not equal to 100%.");
                return;
            }
        }


        commonAccountVM.onCloseEidtPanel();
    }, [commonAccountVM, messageBarVM, tempCompanyRows]);


    const memoServiceCodeList = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "270px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.serviceCode || ''}
                fieldValue={currentEditRow?.serviceCode}
                fieldLabel={COMMON_ACCOUNT_CONSTANT.SERVICE_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'serviceCode'}
                maxLength={60}
                options={commonAccountState.dynamicOptions.serviceCodeDropdownOptions}
                requiredFieldList={commonAccountRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    commonAccountVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [isAdd, isEdit, currentEditRow?.serviceCode, COMMON_ACCOUNT_CONSTANT.SERVICE_CODE, commonAccountState.dynamicOptions.serviceCodeDropdownOptions, commonAccountVM])

    const memoCntrStatusList = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "270px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.cntrStatus || ''}
                fieldValue={currentEditRow?.cntrStatus}
                fieldLabel={COMMON_ACCOUNT_CONSTANT.STATUS}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'cntrStatus'}
                maxLength={60}
                options={statusDropdownOptions}
                requiredFieldList={commonAccountRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    commonAccountVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [isAdd, isEdit, currentEditRow?.cntrStatus, COMMON_ACCOUNT_CONSTANT.STATUS, commonAccountVM])

    const memoSoa = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "270px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.soa || ''}
                fieldValue={currentEditRow?.soa}
                fieldLabel={COMMON_ACCOUNT_CONSTANT.SOA}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'soa'}
                maxLength={60}
                options={commonAccountState.dynamicOptions.soaDropdownOptions}
                requiredFieldList={commonAccountRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    commonAccountVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [isAdd, isEdit, currentEditRow?.soa, COMMON_ACCOUNT_CONSTANT.SOA, commonAccountState.dynamicOptions.soaDropdownOptions, commonAccountVM])

    const memoEtd = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "270px", marginBottom: "24px" }}>
            <DatePickerComponent
                disabled={!(isAdd || isEdit)}
                label={COMMON_ACCOUNT_CONSTANT.ETD}
                width="270px"
                optional={true}
                date={currentEditRow?.etd}
                fieldName="etd"
                errorMessage={""}
                onDateChange={commonAccountVM.onHeaderFieldChange} />
        </div>
        , [COMMON_ACCOUNT_CONSTANT.ETD, commonAccountVM.onHeaderFieldChange, currentEditRow?.etd, isAdd, isEdit])

    const memoActiveInd = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "270px", marginBottom: "24px" }}>
            <NbisCommonField
                fieldKey={"activeInd"}
                requiredFieldList={commonAccountRequiredFieldList}
                fieldLabel={COMMON_ACCOUNT_CONSTANT.ACTIVE_IND}
                fieldType={FieldType.TEXT}
                isReadOnly={true}
                onFieldChange={() => { }} />
            <HPHCheckbox
                label={""}
                checked={currentEditRow?.activeInd === 'Y'}
                disabled={!(isAdd || isEdit)}
                onChange={(e) => commonAccountVM.onCheckboxChange(e.checked, "activeInd")}
            />
        </div>
        , [COMMON_ACCOUNT_CONSTANT.ACTIVE_IND, currentEditRow?.activeInd, isAdd, isEdit, commonAccountVM])

    const memoVesselCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "270px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.vesselCode || ''}
                fieldValue={currentEditRow?.vesselCode}
                fieldLabel={COMMON_ACCOUNT_CONSTANT.VESSEL_CODE}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'vesselCode'}
                maxLength={6}
                requiredFieldList={commonAccountRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    commonAccountVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue, true)} />
        </div>
        , [COMMON_ACCOUNT_CONSTANT.VESSEL_CODE, commonAccountVM, currentEditRow?.vesselCode, isAdd, isEdit])

    const memoVoyageCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "270px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.voyageCode || ''}
                fieldValue={currentEditRow?.voyageCode}
                fieldLabel={COMMON_ACCOUNT_CONSTANT.VOYAGE_CODE}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'voyageCode'}
                maxLength={10}
                requiredFieldList={commonAccountRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    commonAccountVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue, true)} />
        </div>
        , [COMMON_ACCOUNT_CONSTANT.VOYAGE_CODE, commonAccountVM, currentEditRow?.voyageCode, isAdd, isEdit])

    const memoConsortiumCodeList = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "270px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.consortiumCode || ''}
                fieldValue={currentEditRow?.consortiumCode}
                fieldLabel={COMMON_ACCOUNT_CONSTANT.CONSORTIUM_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'consortiumCode'}
                maxLength={60}
                options={commonAccountState.dynamicOptions.consortiumCodeDropdownOptions}
                requiredFieldList={commonAccountRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    commonAccountVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [isAdd, isEdit, currentEditRow?.consortiumCode, COMMON_ACCOUNT_CONSTANT.CONSORTIUM_CODE, commonAccountState.dynamicOptions.consortiumCodeDropdownOptions, commonAccountVM])

    const memoEtb = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "270px", marginBottom: "24px" }}>
            <DatePickerComponent
                disabled={!(isAdd || isEdit)}
                label={COMMON_ACCOUNT_CONSTANT.ETB}
                width="270px"
                optional={true}
                date={currentEditRow?.etb}
                fieldName="etb"
                errorMessage={""}
                onDateChange={commonAccountVM.onHeaderFieldChange} />
        </div>
        , [COMMON_ACCOUNT_CONSTANT.ETB, commonAccountVM.onHeaderFieldChange, currentEditRow?.etb, isAdd, isEdit])


    const handleEdit = useCallback(() => {
        commonAccountVM.onEditClick();
    }, [commonAccountVM]);

    const handleReset = useCallback(() => {
        commonAccountVM.onResetClick(isAdd);
    }, [commonAccountVM, isAdd]);


    return <>
        <StyledSidebar style={{ width: '100%', height: "100%", border: 'none', backgroundColor: '#FFFFFF', overflow: 'auto', padding: '0' }} position='right'>
            <Sidebarheader style={{ width: '100%' }}>
                {isLoading && <Loader Indicator="Spinner" size="Medium" />}
                <Breadcrumb>
                    <HPHBreadcrumb breadcrumbData={[{ title: CommonAccountConstant.Title.CATEGORY },
                    { title: CommonAccountConstant.Title.TITLE }]} onCurrentClick={() => { }}>
                    </HPHBreadcrumb>
                </Breadcrumb>
                <StyledAction>
                    <>
                        {allowUpdate && (!(isAdd || isEdit)) && <NbisIconButton fileName='Icon-pen' size='medium' disabled={false} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Edit'} onClick={handleEdit} />}
                        {(isAdd || isEdit) && <IconButton fileName="Icon-reset" size="medium" disabled={(isShowCompanyAddPanel || isShowCompanyEditPanel)} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Reset'} onClick={() => handleReset()} />}

                        <IconButton fileName="Icon-cross" size="medium" disabled={(isShowCompanyAddPanel || isShowCompanyEditPanel)} toolTipArrow={false} toolTipPlacement='bottom' toolTipText={'Close'} onClick={() => handClose()} />
                        {(isAdd || isEdit) && <IconButton fileName="Icon-tick" size="medium" disabled={(isShowCompanyAddPanel || isShowCompanyEditPanel)} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Save'} onClick={() => handleSave()} />}
                    </>
                </StyledAction>
            </Sidebarheader>

            <div className={'add-edit-form'} style={{ height: 'calc(100vh - 125px)', maxHeight: 'calc(100vh - 125px)', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>
                        <div className={'im-flex-row'}>
                            <div className={'flex-row-item-stretch'}>
                                <SidebarTitle>
                                    {isAdd && "New Common Account"}
                                    {!isAdd && (currentEditRow.priority/* + "/" + currentEditRow.commonAcType*/)}
                                </SidebarTitle>
                                {<SidebarCaption>"All fields are required unless indicated as 'optional'"</SidebarCaption>}
                            </div>
                        </div>

                        <CriteriaItemContainer>
                            {/* add or edit */}
                            {memoVesselCode}
                            {memoVoyageCode}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoSoa}
                            {memoEtd}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoEtb}
                            {memoCntrStatusList}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoConsortiumCodeList}
                            {memoServiceCodeList}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoActiveInd}
                        </CriteriaItemContainer>

                    </div>
                </div>
            </div>
        </StyledSidebar>
    </>
}

export default memo(CommonAccountEditPanel);
